/**
 * ServerDisksPanel — Inventario completo de almacenamiento de un servidor
 *
 * Abre como dv-overlay desde el SmartButton "Discos" de ServerForm.
 * Secciones (solo rellenar las aplicables):
 *   0. Metadatos            — fecha, responsable
 *   1. Controladoras        — RAID HW, HBA, passthrough
 *   2. Discos físicos       — por disco: slot, modelo, capacidad, tipo, salud
 *   3. RAID hardware        — arrays por controladora
 *   4. RAID software        — mdadm, Storage Spaces, etc.
 *   5. Gestión de volúmenes — LVM / ZFS / Storage Spaces
 *   6. Volúmenes lógicos    — dispositivo, filesystem, montaje
 *   7. Relación entre capas — mapa de texto libre
 *   8. Observaciones        — riesgos, alertas
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  PlusCircleIcon,
  TrashIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  CpuChipIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import '../styles/DetailView.css';
import '../styles/ServerDisksPanel.css';
import DetailCrumb from './DetailCrumb';

function getStorageAdvisory(record) {
  const warnings = record?.data?.agent_warnings;
  if (Array.isArray(warnings) && warnings.length > 0) {
    return warnings.find((warning) => warning?.code === 'windows_vroc_storage_review_recommended')
      || warnings.find((warning) => warning?.code === 'windows_vroc_crystaldiskinfo_required')
      || warnings[0];
  }
  const requirements = record?.data?.agent_requirements || {};
  if (requirements.storage_review?.required) {
    return {
      code: 'windows_vroc_storage_review_recommended',
      severity: 'warning',
      title: 'Storage detectado parcialmente: revisar inventario',
      message: requirements.storage_review.reason,
      instructions: requirements.storage_review.instructions || [],
    };
  }
  return null;
}

function getStorageAdvisoryEyebrow(advisory) {
  switch (advisory?.code) {
    case 'windows_vroc_crystaldiskinfo_required':
      return 'Dependencia operativa';
    case 'windows_vroc_storage_review_recommended':
      return 'Revision manual recomendada';
    default:
      return 'Aviso de storage';
  }
}

function diskTypeOf(disk) {
  return disk?.type || disk?.disk_type || (disk?.interface === 'NVMe' ? 'NVMe' : 'HDD');
}

function parseSlotIndex(slot) {
  const text = String(slot || '').trim();
  const match = text.match(/(?:bay|slot|s)\s*(\d+)/i) || text.match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

function canonicalBaySlot(slot) {
  const index = parseSlotIndex(slot);
  return index == null ? String(slot || '').trim() : `Slot ${index}`;
}

function normalizeDiskForUi(rawDisk) {
  const disk = { ...(rawDisk || {}) };
  const type = diskTypeOf(disk);
  return {
    ...disk,
    type,
    disk_type: type,
    health: { ...(disk.health || {}) },
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_CONTROLLER = () => ({ model: '', firmware: '', mode: 'RAID' });
const EMPTY_DISK = (locationType = 'bay') => ({
  slot: '', manufacturer: '', model: '', serial: '',
  capacity_gb: '',
  type: locationType === 'internal' ? 'NVMe' : 'HDD',
  interface: locationType === 'internal' ? 'NVMe' : 'SATA',
  role: locationType === 'internal' ? 'Standalone' : 'RAID',
  location_type: locationType,
  health: { smart_status: 'OK', power_on_hours: '', temperature_c: '', reallocated_sectors: '', wear_level_pct: '', checked_at: '' },
});
const EMPTY_RAID_HW = () => ({
  controller_index: '',
  array_id: '', raid_type: 'RAID1', disk_slots: [], total_gb: '', usable_gb: '',
  status: 'Optimal', hot_spares: '', hot_spare_slots: [], cache_policy: '',
});
const EMPTY_RAID_SW = () => ({
  technology: 'mdadm', name: '', raid_type: 'RAID1', disk_slots: [],
  total_gb: '', usable_gb: '', status: '',
});
const EMPTY_VOLUME = () => ({
  device: '', origin: '', size_gb: '', filesystem: '', mount_point: '',
  usage: '', immutable: false, encrypted: false, encryption_type: '',
});

const EMPTY_DATA = () => ({
  slot_count: 8,
  controllers: [],
  physical_disks: [],
  raid_hw: [],
  raid_sw: [],
  volume_manager: {
    type: null,
    lvm_pvs: '', lvm_vgs: '', lvm_lvs: '', lvm_status: '',
    zfs_pool: '', zfs_vdevs: '', zfs_disks_per_vdev: '', zfs_total_gb: '', zfs_status: '', zfs_snapshots: '', zfs_protected: '',
    ss_pool: '', ss_disks: '', ss_resilience: '', ss_volumes: '', ss_sizes: '', ss_status: '',
  },
  logical_volumes: [],
  layer_map: '',
  observations: { failed_disks: '', degraded_arrays: '', pool_errors: '', special_configs: '', risks: '' },
});

const TABS = [
  { key: 'meta',         label: 'Metadatos' },
  { key: 'controllers',  label: 'Controladoras' },
  { key: 'disks',        label: 'Discos físicos' },
  { key: 'raid_hw',      label: 'RAID Hardware' },
  { key: 'raid_sw',      label: 'RAID Software' },
  { key: 'volmgr',       label: 'LVM / ZFS / SS' },
  { key: 'volumes',      label: 'Volúmenes lógicos' },
  { key: 'layers',       label: 'Capas' },
  { key: 'observations', label: 'Observaciones' },
];

// ── RAID rules ───────────────────────────────────────────────────────────────

const RAID_RULES = {
  'RAID0':  { min: 2, max: null, even: false, label: 'Striping sin redundancia',      hint: 'Mín. 2 discos. Sin redundancia. Usable = suma total. 1 fallo = pérdida total.' },
  'RAID1':  { min: 2, max: 2,    even: false, label: 'Mirror',                         hint: 'Exactamente 2 discos. Usable = disco más pequeño. Tolera 1 fallo.' },
  'RAID5':  { min: 3, max: null, even: false, label: 'Striping + paridad distribuida', hint: 'Mín. 3 discos. Usable = (N−1) × min. Tolera 1 fallo.' },
  'RAID6':  { min: 4, max: null, even: false, label: 'Striping + doble paridad',       hint: 'Mín. 4 discos. Usable = (N−2) × min. Tolera 2 fallos simultáneos.' },
  'RAID10': { min: 4, max: null, even: true,  label: 'Mirror + Striping (1+0)',        hint: 'Mín. 4 discos, número par. Usable = total ÷ 2. Alto rendimiento y redundancia.' },
  'RAID50': { min: 6, max: null, even: true,  label: 'RAID5 + Striping (5+0)',         hint: 'Mín. 6 discos, número par. 2 grupos RAID5. Usable = (N−2) × min.' },
  'RAID60': { min: 8, max: null, even: true,  label: 'RAID6 + Striping (6+0)',         hint: 'Mín. 8 discos, número par. 2 grupos RAID6. Usable = (N−4) × min. Tolera 4 fallos.' },
  'JBOD':   { min: 1, max: null, even: false, label: 'Just a Bunch of Disks',          hint: 'Cualquier número. Sin redundancia ni paridad. Usable = suma total.' },
};

function calcRaidSizes(raidType, capacities) {
  if (!capacities || capacities.length === 0) return null;
  const n = capacities.length;
  const total = capacities.reduce((s, c) => s + c, 0);
  const min = Math.min(...capacities);
  switch (raidType) {
    case 'RAID0':  return { total, usable: total };
    case 'RAID1':  return { total, usable: min };
    case 'RAID5':  return n >= 3 ? { total, usable: (n - 1) * min } : null;
    case 'RAID6':  return n >= 4 ? { total, usable: (n - 2) * min } : null;
    case 'RAID10': return n >= 4 && n % 2 === 0 ? { total, usable: Math.floor(total / 2) } : null;
    case 'RAID50': return n >= 6 && n % 2 === 0 ? { total, usable: (n - 2) * min } : null;
    case 'RAID60': return n >= 8 && n % 2 === 0 ? { total, usable: (n - 4) * min } : null;
    case 'JBOD':   return { total, usable: total };
    default:       return null;
  }
}

function validateRaid(raidType, n) {
  const rule = RAID_RULES[raidType];
  if (!rule || n === 0) return [];
  const errors = [];
  if (n < rule.min)
    errors.push(`${raidType} requiere mínimo ${rule.min} disco${rule.min > 1 ? 's' : ''} — hay ${n}`);
  if (rule.max !== null && n > rule.max)
    errors.push(`${raidType} soporta máximo ${rule.max} disco${rule.max > 1 ? 's' : ''} — hay ${n}`);
  if (rule.even && n % 2 !== 0)
    errors.push(`${raidType} requiere número par de discos — hay ${n}`);
  return errors;
}

function buildCalcHint(raidType, caps, result) {
  const n = caps.length;
  const min = Math.min(...caps);
  switch (raidType) {
    case 'RAID0':  return `${n} × ${min} GB = ${result.usable} GB usable (sin paridad)`;
    case 'RAID1':  return `min(${caps.join(', ')}) = ${result.usable} GB usable (mirror)`;
    case 'RAID5':  return `(${n}−1) × ${min} GB = ${result.usable} GB usable`;
    case 'RAID6':  return `(${n}−2) × ${min} GB = ${result.usable} GB usable`;
    case 'RAID10': return `${result.total} GB ÷ 2 = ${result.usable} GB usable`;
    case 'RAID50': return `(${n}−2) × ${min} GB = ${result.usable} GB usable`;
    case 'RAID60': return `(${n}−4) × ${min} GB = ${result.usable} GB usable`;
    case 'JBOD':   return `${n} × ${min} GB = ${result.usable} GB usable`;
    default:       return `${result.usable} GB usable`;
  }
}

// ── SubForms ──────────────────────────────────────────────────────────────────

function ControllerRow({ ctrl, onChange, onRemove }) {
  return (
    <div className="dv-section" style={{ position: 'relative', marginBottom: '0.75rem' }}>
      <div className="row g-2">
        <div className="col-md-5">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Modelo / Fabricante</label>
          <input className="form-control form-control-sm" value={ctrl.model}
            onChange={e => onChange({ ...ctrl, model: e.target.value })} placeholder="HP Smart Array P408i-a" />
        </div>
        <div className="col-md-3">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Firmware</label>
          <input className="form-control form-control-sm" value={ctrl.firmware}
            onChange={e => onChange({ ...ctrl, firmware: e.target.value })} placeholder="5.00" />
        </div>
        <div className="col-md-3">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Modo</label>
          <select className="form-select form-select-sm" value={ctrl.mode}
            onChange={e => onChange({ ...ctrl, mode: e.target.value })}>
            <option>RAID</option>
            <option>HBA</option>
            <option>Passthrough</option>
            <option>Software</option>
          </select>
        </div>
        <div className="col-md-1 d-flex align-items-end">
          <button type="button" className="dv-btn dv-btn-danger" style={{ padding: '0.25rem 0.4rem' }} onClick={onRemove}>
            <TrashIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers for disk cards ────────────────────────────────────────────────────
const TYPE_ICON = { SSD: '⚡', NVMe: '🚀', HDD: '💿' };
const SMART_TONE = { OK: 'ok', Warning: 'warning', Failed: 'failed' };
const ROLE_TONE = {
  RAID: 'indigo',
  'RAID member': 'indigo',
  'Hot spare': 'amber',
  JBOD: 'sky',
  Passthrough: 'muted',
  Standalone: 'green',
  Retirado: 'muted',
};
const fmtCap = gb => {
  if (!gb) return '—';
  const n = Number(gb);
  return n >= 1024 ? `${(n / 1024).toFixed(1)} TB` : `${n} GB`;
};
const slotNum = s => {
  const m = (s || '').match(/\d+/);
  return m ? parseInt(m[0]) : 9999;
};

// ── DiskKanbanCard ────────────────────────────────────────────────────────────
function DiskKanbanCard({ disk, onEdit, onRemove, onRetire, onCopy, onCopyTo }) {
  const [hover, setHover] = useState(false);
  const h = disk.health || {};
  const smartTone = SMART_TONE[h.smart_status] || 'neutral';
  const isRetired = disk.role === 'Retirado';
  const isInternal = disk.location_type === 'internal';
  const roleTone = ROLE_TONE[disk.role] || 'green';
  const diskType = diskTypeOf(disk);
  const slotLabel = isInternal
    ? `📌 ${disk.slot || 'Interno'}`
    : (disk.slot || (isRetired ? 'Retirado' : '—'));

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`sdp-disk-card${hover ? ' is-hovered' : ''}${isRetired ? ' is-retired' : ''}${isInternal ? ' is-internal' : ''}`}
    >
      <div className="sdp-card-topline">
        <span className="sdp-card-slot">
          {slotLabel}
        </span>
        <span className="sdp-card-type" title={`${diskType} · ${disk.interface}`}>
          {TYPE_ICON[diskType] || '💿'} {diskType}
        </span>
      </div>

      <div className="sdp-card-capacity">
        {fmtCap(disk.capacity_gb)}
      </div>

      <div className="sdp-card-model"
        title={[disk.manufacturer, disk.model].filter(Boolean).join(' ')}>
        {[disk.manufacturer, disk.model].filter(Boolean).join(' ') || <em className="sdp-card-placeholder">Sin modelo</em>}
      </div>

      <div className="sdp-card-chip-row">
        <span className="sdp-chip sdp-chip-muted">
          {disk.interface}
        </span>
        <span className={`sdp-chip sdp-chip-${roleTone}`}>
          {disk.role}
        </span>
      </div>

      <div className="sdp-card-health-row">
        <span className={`sdp-smart sdp-smart-${smartTone}`}>
          <span className="sdp-smart-dot" />
          SMART {h.smart_status || '—'}
        </span>
        {h.temperature_c && (
          <span className="sdp-card-temp">· {h.temperature_c}°C</span>
        )}
      </div>

      {disk.serial && (
        <div className="sdp-card-serial"
          title={`S/N: ${disk.serial}`}>
          S/N: {disk.serial}
        </div>
      )}

      {hover && (
        <div className="sdp-card-actions">
          {onCopy && (
            <button type="button" onClick={onCopy}
              className="sdp-icon-btn sdp-icon-btn-indigo"
              title="Copiar disco a este mismo servidor (sin slot ni S/N)">
              <DocumentDuplicateIcon />
            </button>
          )}
          {onCopyTo && (
            <button type="button" onClick={onCopyTo}
              className="sdp-icon-btn sdp-icon-btn-green"
              title="Copiar disco a otro servidor (sin slot ni S/N)">
              <ArrowTopRightOnSquareIcon />
            </button>
          )}
          <button type="button" onClick={onEdit}
            className="sdp-icon-btn sdp-icon-btn-violet"
            title="Editar">
            <PencilSquareIcon />
          </button>
          {onRetire && !isRetired && !isInternal && (
            <button type="button" onClick={onRetire}
              className="sdp-icon-btn sdp-icon-btn-amber"
              title="Retirar disco del slot">
              <ArrowLeftIcon />
            </button>
          )}
          <button type="button" onClick={onRemove}
            className="sdp-icon-btn sdp-icon-btn-red"
            title="Eliminar del inventario">
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  );
}

// ── DiskEditModal ─────────────────────────────────────────────────────────────
function DiskEditModal({ disk, onSave, onClose, availableSlots }) {
  const [d, setD] = useState(disk);
  const h = d.health || {};
  const setH = (field, val) => setD(prev => ({ ...prev, health: { ...prev.health, [field]: val } }));

  return (
    <div className="sdp-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sdp-modal-shell sdp-modal-shell-disk">
        <div className="sdp-modal-header">
          <div className="sdp-modal-title-wrap">
            <CpuChipIcon className="sdp-modal-title-icon sdp-modal-title-icon-violet" />
            <h4 className="sdp-modal-title">
              {d.slot
                ? ((d.location_type || 'bay') === 'internal' ? `Disco interno — ${d.slot}` : `Disco — ${d.slot}`)
                : ((d.location_type || 'bay') === 'internal' ? 'Nuevo disco interno' : 'Nuevo disco físico')}
            </h4>
          </div>
          <button type="button" onClick={onClose} className="sdp-modal-close-btn">
            <XMarkIcon />
          </button>
        </div>

        <div className="sdp-section-label">
          Identificación
        </div>
        <div className="row g-2 mb-2">
          <div className="col-12">
            <label className="form-label sdp-form-label">Ubicación</label>
            <div className="sdp-toggle-group">
              {[{ val: 'bay', label: '🗄️ Bay / Slot' }, { val: 'internal', label: '📌 Interno (onboard)' }].map(({ val, label }) => (
                <button key={val} type="button"
                  onClick={() => setD(p => ({ ...p, location_type: val }))}
                  className={`sdp-toggle-btn${(d.location_type || 'bay') === val ? ' active' : ''}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>
              {(d.location_type || 'bay') === 'internal' ? 'ID interno' : 'Slot / Bay'}
            </label>
            {(d.location_type || 'bay') === 'internal' ? (
              <input className="form-control form-control-sm" value={d.slot}
                onChange={e => setD(p => ({ ...p, slot: e.target.value }))} placeholder="M.2_1 / DOM1 / SATA_SB0" />
            ) : availableSlots && availableSlots.length > 0 ? (
              <select className="form-select form-select-sm" value={canonicalBaySlot(d.slot)}
                onChange={e => setD(p => ({ ...p, slot: e.target.value }))}>
                <option value="">— seleccionar —</option>
                {availableSlots.map(s => (
                  <option key={s.label} value={s.label} disabled={s.occupied && s.label !== canonicalBaySlot(disk.slot)}>
                    {s.label}{s.occupied && s.label !== canonicalBaySlot(disk.slot) ? ' (ocupado)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input className="form-control form-control-sm" value={d.slot}
                onChange={e => setD(p => ({ ...p, slot: e.target.value }))} placeholder="Slot 0" />
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Fabricante</label>
            <input className="form-control form-control-sm" value={d.manufacturer}
              onChange={e => setD(p => ({ ...p, manufacturer: e.target.value }))} placeholder="Seagate" />
          </div>
          <div className="col-md-4">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Modelo</label>
            <input className="form-control form-control-sm" value={d.model}
              onChange={e => setD(p => ({ ...p, model: e.target.value }))} placeholder="ST4000NM0115" />
          </div>
          <div className="col-md-5">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Número de serie</label>
            <input className="form-control form-control-sm" value={d.serial}
              onChange={e => setD(p => ({ ...p, serial: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Capacidad (GB)</label>
            <input className="form-control form-control-sm" type="number" min="0" value={d.capacity_gb}
              onChange={e => setD(p => ({ ...p, capacity_gb: e.target.value }))} placeholder="4000" />
          </div>
        </div>

        <div className="sdp-section-label">
          Tipo y rol
        </div>
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Tipo</label>
            <select className="form-select form-select-sm" value={d.type}
              onChange={e => setD(p => ({ ...p, type: e.target.value }))}>
              <option>HDD</option>
              <option>SSD</option>
              <option>NVMe</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Interfaz</label>
            <select className="form-select form-select-sm" value={d.interface}
              onChange={e => setD(p => ({ ...p, interface: e.target.value }))}>
              <option>SATA</option>
              <option>SAS</option>
              <option>NVMe</option>
              <option>U.2</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Rol</label>
            <select className="form-select form-select-sm" value={d.role}
              onChange={e => setD(p => ({ ...p, role: e.target.value }))}>
              <option>RAID member</option>
              <option>RAID</option>
              <option>JBOD</option>
              <option>Passthrough</option>
              <option>Standalone</option>
              <option>Hot spare</option>
              <option>Retirado</option>
            </select>
          </div>
        </div>

        <div className="sdp-health-panel">
          <div className="sdp-health-title">
            🩺 Estado de salud — dato puntual
          </div>
          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>SMART</label>
              <select className="form-select form-select-sm" value={h.smart_status}
                onChange={e => setH('smart_status', e.target.value)}>
                <option>OK</option>
                <option>Warning</option>
                <option>Failed</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Horas (POH)</label>
              <input className="form-control form-control-sm" type="number" min="0" value={h.power_on_hours}
                onChange={e => setH('power_on_hours', e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Temperatura °C</label>
              <input className="form-control form-control-sm" type="number" value={h.temperature_c}
                onChange={e => setH('temperature_c', e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Sect. reasignados</label>
              <input className="form-control form-control-sm" type="number" min="0" value={h.reallocated_sectors}
                onChange={e => setH('reallocated_sectors', e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Wear Level % (SSD)</label>
              <input className="form-control form-control-sm" type="number" min="0" max="100" value={h.wear_level_pct}
                onChange={e => setH('wear_level_pct', e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Fecha comprobación</label>
              <input className="form-control form-control-sm" type="date" value={h.checked_at}
                onChange={e => setH('checked_at', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="sdp-modal-actions">
          <button type="button" className="dv-btn dv-btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="dv-btn dv-btn-primary" onClick={() => onSave(d)}>
            <CheckIcon style={{ width: 14, height: 14 }} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper: check if a disk_slots entry matches a physical disk (by slot or serial)
function _diskMatchesSlotEntry(disk, idx, entry) {
  if (!entry) return false;
  const id = disk.slot || `Disco ${idx + 1}`;
  if (id === entry) return true;
  const serial = (disk.serial || '').trim();
  return serial && serial === entry;
}

// Normalize disk_slots: replace serials with slot names where possible
function normalizeDiskSlots(diskSlots, physicalDisks) {
  if (!diskSlots || !physicalDisks) return diskSlots || [];
  return diskSlots.map(entry => {
    const match = physicalDisks.find((d, i) => {
      const serial = (d.serial || '').trim();
      return serial && serial === entry && (d.slot || `Disco ${i + 1}`) !== entry;
    });
    return match ? (match.slot || entry) : entry;
  });
}

function DiskSelector({ physicalDisks, selected, onChange, busySlots = [] }) {
  const sel = Array.isArray(selected) ? selected : [];
  const busy = new Set(busySlots);
  const toggle = (slot) => {
    if (busy.has(slot)) return;
    // Remove: filter out any entry matching this slot (by slot or serial)
    const already = sel.find(entry => {
      const match = physicalDisks.find((d, i) => (d.slot || `Disco ${i + 1}`) === slot);
      return match ? _diskMatchesSlotEntry(match, physicalDisks.indexOf(match), entry) : entry === slot;
    });
    if (already) onChange(sel.filter(s => s !== already));
    else onChange([...sel, slot]);
  };
  if (!physicalDisks || physicalDisks.length === 0) {
    return (
      <div className="sdp-field-empty-hint">
        Añade discos en la pestaña &quot;Discos físicos&quot; primero.
      </div>
    );
  }
  return (
    <div className="sdp-selector-chips">
      {physicalDisks.map((disk, i) => {
        const id = disk.slot || `Disco ${i + 1}`;
        const label = [id, disk.model && disk.model !== id ? disk.model : null, disk.capacity_gb ? `${disk.capacity_gb}GB` : null].filter(Boolean).join(' · ');
        const checked = sel.some(entry => _diskMatchesSlotEntry(disk, i, entry));
        const isBusy = busy.has(id) && !checked;
        return (
          <label key={i} title={isBusy ? 'Este disco ya está asignado a otro RAID' : undefined}
            className={`sdp-selector-chip${checked ? ' is-selected' : ''}${isBusy ? ' is-busy' : ''}`}>
            <input type="checkbox" checked={checked} disabled={isBusy} onChange={() => toggle(id)} className="sdp-selector-checkbox" />
            {label}{isBusy ? ' ⚠' : ''}
          </label>
        );
      })}
    </div>
  );
}

function RaidHwRow({ arr, onChange, onRemove, physicalDisks, controllers = [], showRemove = true, busySlots = [] }) {
  const slotsKey = (arr.disk_slots || []).join(',');

  // Auto-calculate sizes when disk selection or RAID type changes
  useEffect(() => {
    const slots = arr.disk_slots || [];
    if (slots.length === 0) return;
    const caps = slots.map(entry => {
      const d = physicalDisks.find((d, i) => _diskMatchesSlotEntry(d, i, entry));
      return d?.capacity_gb ? Number(d.capacity_gb) : 0;
    }).filter(c => c > 0);
    if (caps.length === 0) return;
    const result = calcRaidSizes(arr.raid_type, caps);
    if (!result) return;
    const newTotal = String(Math.round(result.total));
    const newUsable = String(Math.round(result.usable));
    if (newTotal !== String(arr.total_gb) || newUsable !== String(arr.usable_gb)) {
      onChange({ ...arr, total_gb: newTotal, usable_gb: newUsable });
    }
  }, [slotsKey, arr.raid_type]); // eslint-disable-line

  const n = (arr.disk_slots || []).length;
  const errors = validateRaid(arr.raid_type, n);
  const rule = RAID_RULES[arr.raid_type];

  const resolvedCaps = (arr.disk_slots || []).map((entry) => {
    const d = physicalDisks.find((d, i) => _diskMatchesSlotEntry(d, i, entry));
    return d?.capacity_gb ? Number(d.capacity_gb) : null;
  }).filter(c => c !== null);
  const calcResult = resolvedCaps.length > 0 ? calcRaidSizes(arr.raid_type, resolvedCaps) : null;
  const calcHint = calcResult ? buildCalcHint(arr.raid_type, resolvedCaps, calcResult) : null;

  return (
    <div className={`dv-section sdp-array-editor${errors.length > 0 ? ' has-errors' : ''}`}>
      <div className="sdp-array-editor-header">
        {rule && (
          <span className="sdp-array-editor-hint">
            <strong>{rule.label}</strong> — {rule.hint}
          </span>
        )}
        {showRemove && (
          <button type="button" className="dv-btn dv-btn-danger sdp-compact-danger-btn" onClick={onRemove}>
            <TrashIcon style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>
      {errors.length > 0 && (
        <div className="sdp-array-errors">
          {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}
      <div className="row g-2">
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>ID Array</label>
          <input className="form-control form-control-sm" value={arr.array_id}
            onChange={e => onChange({ ...arr, array_id: e.target.value })} placeholder="A:0" />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Tipo RAID</label>
          <select className="form-select form-select-sm" value={arr.raid_type}
            onChange={e => onChange({ ...arr, raid_type: e.target.value })}>
            {['RAID0','RAID1','RAID5','RAID6','RAID10','RAID50','RAID60','JBOD'].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Controladora</label>
          <select
            className="form-select form-select-sm"
            value={arr.controller_index ?? ''}
            onChange={e => onChange({ ...arr, controller_index: e.target.value })}
          >
            <option value="">— seleccionar controladora —</option>
            {controllers.map((c, i) => (
              <option key={i} value={String(i)}>
                C{i + 1} · {c.model || 'Sin modelo'}{c.firmware ? ` · FW ${c.firmware}` : ''} ({c.mode || 'RAID'})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-12">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Discos miembros del array</label>
          <DiskSelector physicalDisks={physicalDisks} selected={arr.disk_slots || []} busySlots={busySlots}
            onChange={slots => onChange({ ...arr, disk_slots: slots })} />
          {calcHint && errors.length === 0 && (
            <div className="sdp-array-calc-hint">✓ {calcHint}</div>
          )}
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Total (GB) <span className="sdp-auto-label">auto</span></label>
          <input className={`form-control form-control-sm${arr.total_gb ? ' sdp-auto-input' : ''}`} type="number" min="0" value={arr.total_gb}
            onChange={e => onChange({ ...arr, total_gb: e.target.value })} />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Usable (GB) <span className="sdp-auto-label">auto</span></label>
          <input className={`form-control form-control-sm${arr.usable_gb ? ' sdp-auto-input' : ''}`} type="number" min="0" value={arr.usable_gb}
            onChange={e => onChange({ ...arr, usable_gb: e.target.value })} />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Estado</label>
          <select className="form-select form-select-sm" value={arr.status}
            onChange={e => onChange({ ...arr, status: e.target.value })}>
            <option>Optimal</option>
            <option>Degraded</option>
            <option>Rebuilding</option>
            <option>Failed</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Hot spares</label>
          {(() => {
            const spareDisks = physicalDisks.filter(d => d.role === 'Hot spare');
            const selectedSpares = Array.isArray(arr.hot_spare_slots) ? arr.hot_spare_slots : [];
            if (spareDisks.length === 0) {
              return (
                <div className="sdp-field-empty-hint">
                  Sin discos con rol &quot;Hot spare&quot;
                </div>
              );
            }
            return (
              <div className="sdp-selector-chips">
                {spareDisks.map((disk, i) => {
                  const id = disk.slot || `Disco ${physicalDisks.indexOf(disk) + 1}`;
                  const checked = selectedSpares.includes(id);
                  return (
                    <label key={i} className={`sdp-selector-chip sdp-selector-chip-spare${checked ? ' is-selected' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => {
                        const next = checked ? selectedSpares.filter(s => s !== id) : [...selectedSpares, id];
                        onChange({ ...arr, hot_spare_slots: next, hot_spares: next.join(', ') });
                      }} className="sdp-selector-checkbox" />
                      {id}{disk.capacity_gb ? ` · ${disk.capacity_gb}GB` : ''}
                    </label>
                  );
                })}
              </div>
            );
          })()}
        </div>
        <div className="col-md-3">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Política de caché</label>
          <input className="form-control form-control-sm" value={arr.cache_policy}
            onChange={e => onChange({ ...arr, cache_policy: e.target.value })} placeholder="Write Back / Read Ahead" />
        </div>
      </div>
    </div>
  );
}

function RaidHwKanbanCard({ arr, idx, controllers, onEdit, onRemove }) {
  const ctrl = arr.controller_index !== '' && arr.controller_index != null
    ? controllers[Number(arr.controller_index)]
    : null;
  const disks = (arr.disk_slots || []).length;

  return (
    <div className="sdp-raid-card">
      <div className="sdp-raid-card-topline">
        <span className="sdp-chip sdp-chip-muted">
          {arr.array_id || `Array ${idx + 1}`}
        </span>
        <span className="sdp-chip sdp-chip-indigo">
          {arr.raid_type || 'RAID'}
        </span>
      </div>

      <div className="sdp-raid-card-capacity">
        {arr.usable_gb || '—'} GB
      </div>
      <div className="sdp-raid-card-meta">
        {arr.total_gb || '—'} GB total · {disks} discos
      </div>

      <div className="sdp-raid-card-controller">
        <strong>Controladora:</strong>{' '}
        {ctrl ? `C${Number(arr.controller_index) + 1} · ${ctrl.model || 'Sin modelo'}` : 'Sin asignar'}
      </div>

      <div className="sdp-raid-card-members" title={(arr.disk_slots || []).join(', ')}>
        {(arr.disk_slots || []).length > 0 ? (arr.disk_slots || []).join(', ') : 'Sin discos miembros'}
      </div>

      {(arr.hot_spare_slots || []).length > 0 && (
        <div className="sdp-raid-card-spare">
          🛡 Spare: {arr.hot_spare_slots.join(', ')}
        </div>
      )}
      {!arr.hot_spare_slots?.length && arr.hot_spares && (
        <div className="sdp-raid-card-spare">
          🛡 Spare: {arr.hot_spares}
        </div>
      )}

      <div className="sdp-raid-card-footer">
        <span className="sdp-chip sdp-chip-muted">
          {arr.status || '—'}
        </span>
        <div className="sdp-inline-actions">
          <button type="button" onClick={onEdit} className="sdp-icon-btn sdp-icon-btn-violet" title="Editar">
            <PencilSquareIcon />
          </button>
          <button type="button" onClick={onRemove} className="sdp-icon-btn sdp-icon-btn-red" title="Eliminar">
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

function RaidHwEditModal({ arr, controllers, physicalDisks, busySlots = [], onSave, onClose }) {
  const [draft, setDraft] = useState(arr || EMPTY_RAID_HW());

  useEffect(() => {
    setDraft(arr || EMPTY_RAID_HW());
  }, [arr]);

  return (
    <div
      className="sdp-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sdp-modal-shell sdp-modal-shell-array">
        <div className="sdp-modal-header sdp-modal-header-compact">
          <h4 className="sdp-modal-title">Editar array RAID hardware</h4>
          <button type="button" onClick={onClose} className="sdp-modal-close-btn">
            <XMarkIcon />
          </button>
        </div>

        <RaidHwRow
          arr={draft}
          controllers={controllers}
          physicalDisks={physicalDisks}
          busySlots={busySlots}
          showRemove={false}
          onChange={setDraft}
        />

        <div className="sdp-modal-actions">
          <button type="button" className="dv-btn dv-btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="dv-btn dv-btn-primary" onClick={() => onSave(draft)}>
            <CheckIcon style={{ width: 14, height: 14 }} /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function RaidSwRow({ arr, onChange, onRemove, physicalDisks, busySlots = [] }) {
  const slotsKey = (arr.disk_slots || []).join(',');

  useEffect(() => {
    const slots = arr.disk_slots || [];
    if (slots.length === 0) return;
    const caps = slots.map(slot => {
      const d = physicalDisks.find((d, i) => (d.slot || `Disco ${i + 1}`) === slot);
      return d?.capacity_gb ? Number(d.capacity_gb) : 0;
    }).filter(c => c > 0);
    if (caps.length === 0) return;
    const result = calcRaidSizes(arr.raid_type, caps);
    if (!result) return;
    const newTotal = String(Math.round(result.total));
    const newUsable = String(Math.round(result.usable));
    if (newTotal !== String(arr.total_gb) || newUsable !== String(arr.usable_gb)) {
      onChange({ ...arr, total_gb: newTotal, usable_gb: newUsable });
    }
  }, [slotsKey, arr.raid_type]); // eslint-disable-line

  const n = (arr.disk_slots || []).length;
  const errors = validateRaid(arr.raid_type, n);
  const rule = RAID_RULES[arr.raid_type];

  const resolvedCaps2 = (arr.disk_slots || []).map((slot) => {
    const d = physicalDisks.find((d, i) => (d.slot || `Disco ${i + 1}`) === slot);
    return d?.capacity_gb ? Number(d.capacity_gb) : null;
  }).filter(c => c !== null);
  const calcResult2 = resolvedCaps2.length > 0 ? calcRaidSizes(arr.raid_type, resolvedCaps2) : null;
  const calcHint2 = calcResult2 ? buildCalcHint(arr.raid_type, resolvedCaps2, calcResult2) : null;

  return (
    <div className={`sdp-array-editor${errors.length > 0 ? ' has-errors' : ''}`}>
      <div className="sdp-array-editor-header">
        {rule && (
          <span className="sdp-array-editor-hint">
            <strong>{rule.label}</strong> — {rule.hint}
          </span>
        )}
        <button type="button" className="dv-btn dv-btn-danger sdp-compact-danger-btn" onClick={onRemove}>
          <TrashIcon style={{ width: 13, height: 13 }} />
        </button>
      </div>
      {errors.length > 0 && (
        <div className="sdp-array-errors">
          {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}
      <div className="row g-2">
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Tecnología</label>
          <select className="form-select form-select-sm" value={arr.technology}
            onChange={e => onChange({ ...arr, technology: e.target.value })}>
            <option>mdadm</option>
            <option>Storage Spaces</option>
            <option>vinum</option>
            <option>otro</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Nombre</label>
          <input className="form-control form-control-sm" value={arr.name}
            onChange={e => onChange({ ...arr, name: e.target.value })} placeholder="/dev/md0" />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Tipo RAID</label>
          <select className="form-select form-select-sm" value={arr.raid_type}
            onChange={e => onChange({ ...arr, raid_type: e.target.value })}>
            {['RAID0','RAID1','RAID5','RAID6','RAID10','RAID50','RAID60'].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="col-md-12">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Discos miembros del array</label>
          <DiskSelector physicalDisks={physicalDisks} selected={arr.disk_slots || []} busySlots={busySlots}
            onChange={slots => onChange({ ...arr, disk_slots: slots })} />
          {calcHint2 && errors.length === 0 && (
            <div className="sdp-array-calc-hint">✓ {calcHint2}</div>
          )}
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Total (GB) <span className="sdp-auto-label">auto</span></label>
          <input className={`form-control form-control-sm${arr.total_gb ? ' sdp-auto-input' : ''}`} type="number" min="0" value={arr.total_gb}
            onChange={e => onChange({ ...arr, total_gb: e.target.value })} />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Usable (GB) <span className="sdp-auto-label">auto</span></label>
          <input className={`form-control form-control-sm${arr.usable_gb ? ' sdp-auto-input' : ''}`} type="number" min="0" value={arr.usable_gb}
            onChange={e => onChange({ ...arr, usable_gb: e.target.value })} />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Estado</label>
          <input className="form-control form-control-sm" value={arr.status}
            onChange={e => onChange({ ...arr, status: e.target.value })} placeholder="clean" />
        </div>
      </div>
    </div>
  );
}

function VolumeRow({ vol, idx, onChange, onRemove, sources }) {
  return (
    <div className="dv-section" style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
        <button type="button" className="dv-btn dv-btn-danger" style={{ padding: '0.2rem 0.4rem' }} onClick={onRemove}>
          <TrashIcon style={{ width: 13, height: 13 }} />
        </button>
      </div>
      <div className="row g-2">
        <div className="col-md-3">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Dispositivo</label>
          <input className="form-control form-control-sm" value={vol.device}
            onChange={e => onChange({ ...vol, device: e.target.value })} placeholder="/dev/sda1 · C: · /dev/md0" />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Origen</label>
          <input className="form-control form-control-sm"
            list={`vol-origins-${idx}`}
            value={vol.origin}
            onChange={e => onChange({ ...vol, origin: e.target.value })}
            placeholder="RAID HW · LVM · ZFS" />
          {sources && sources.length > 0 && (
            <datalist id={`vol-origins-${idx}`}>
              {sources.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
            </datalist>
          )}
        </div>
        <div className="col-md-1">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Tamaño (GB)</label>
          <input className="form-control form-control-sm" type="number" min="0" value={vol.size_gb}
            onChange={e => onChange({ ...vol, size_gb: e.target.value })} />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Filesystem</label>
          <input className="form-control form-control-sm" value={vol.filesystem}
            onChange={e => onChange({ ...vol, filesystem: e.target.value })} placeholder="ext4 · NTFS · xfs · ZFS" />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Punto de montaje</label>
          <input className="form-control form-control-sm" value={vol.mount_point}
            onChange={e => onChange({ ...vol, mount_point: e.target.value })} placeholder="/ · /data · D:\" />
        </div>
        <div className="col-md-2">
          <label className="form-label" style={{ fontSize: '0.8rem' }}>Uso</label>
          <input className="form-control form-control-sm" value={vol.usage}
            onChange={e => onChange({ ...vol, usage: e.target.value })} placeholder="sistema · datos · logs · swap" />
        </div>
        <div className="col-md-12 d-flex gap-3 mt-1">
          <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={vol.immutable}
              onChange={e => onChange({ ...vol, immutable: e.target.checked })} />
            Inmutable / protegido
          </label>
          <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={vol.encrypted}
              onChange={e => onChange({ ...vol, encrypted: e.target.checked })} />
            Cifrado
          </label>
          {vol.encrypted && (
            <input className="form-control form-control-sm" style={{ width: 150 }} value={vol.encryption_type}
              onChange={e => onChange({ ...vol, encryption_type: e.target.value })}
              placeholder="BitLocker / LUKS" />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Layer map auto-generator ─────────────────────────────────────────────────

function generateLayerMap(data) {
  const lines = [];
  const disks = data.physical_disks || [];
  const raidHw = data.raid_hw || [];
  const raidSw = data.raid_sw || [];
  const vm = data.volume_manager || {};
  const vols = data.logical_volumes || [];

  const bayDisks = disks.filter(d => (d.location_type || 'bay') === 'bay');
  const internalDisks = disks.filter(d => d.location_type === 'internal');
  if (bayDisks.length > 0) {
    lines.push('── DISCOS FÍSICOS (BAHÍA) ──');
    bayDisks.forEach(disk => {
      const id = disk.slot || '?';
      const serial = (disk.serial || '').trim();
      const matchSlot = (slots) => slots.includes(id) || (serial && slots.some(s => s === serial));
      const desc = [disk.manufacturer, disk.model, disk.capacity_gb && `${disk.capacity_gb}GB`, disk.type].filter(Boolean).join(' ');
      const inRaidHw = raidHw.filter(r => matchSlot(r.disk_slots || [])).map(r => `RAID HW ${r.array_id || '?'} (${r.raid_type})`);
      const inRaidSw = raidSw.filter(r => matchSlot(r.disk_slots || [])).map(r => `RAID SW ${r.name || '?'} (${r.raid_type})`);
      const inSpare = raidHw.filter(r => matchSlot(r.hot_spare_slots || [])).map(r => `Hot Spare → ${r.array_id || '?'} (${r.raid_type})`);
      const assignments = [...inRaidHw, ...inRaidSw, ...inSpare];
      lines.push(`  [${id}]  ${desc || '—'}  →  ${assignments.length ? assignments.join(', ') : 'Sin asignar'}`);
    });
    lines.push('');
  }
  if (internalDisks.length > 0) {
    lines.push('── DISCOS INTERNOS (ONBOARD) ──');
    internalDisks.forEach(disk => {
      const id = disk.slot || '?';
      const desc = [disk.manufacturer, disk.model, disk.capacity_gb && `${disk.capacity_gb}GB`, disk.type].filter(Boolean).join(' ');
      lines.push(`  [${id}]  ${desc || '—'}  →  ${disk.role || 'Standalone'}`);
    });
    lines.push('');
  }

  if (raidHw.length > 0) {
    lines.push('── RAID HARDWARE ──');
    raidHw.forEach(arr => {
      const slots = arr.disk_slots || [];
      const ctrl = arr.controller_index !== '' && arr.controller_index != null
        ? `Ctrl #${Number(arr.controller_index) + 1}`
        : 'Ctrl ?';
      lines.push(`  [${arr.array_id || '?'}]  ${arr.raid_type}  —  ${slots.length} disco(s)  —  ${arr.total_gb || '?'}GB total / ${arr.usable_gb || '?'}GB usable  —  ${arr.status}`);
      lines.push(`    └─ Controladora: ${ctrl}`);
      if (slots.length > 0) lines.push(`    └─ Miembros: ${slots.join(', ')}`);
      const spareList = (arr.hot_spare_slots || []).length > 0 ? arr.hot_spare_slots.join(', ') : arr.hot_spares || '';
      if (spareList) lines.push(`    └─ Hot spare: ${spareList}`);
    });
    lines.push('');
  }

  if (raidSw.length > 0) {
    lines.push('── RAID SOFTWARE ──');
    raidSw.forEach(arr => {
      const slots = arr.disk_slots || [];
      lines.push(`  [${arr.name || '?'}]  ${arr.raid_type} (${arr.technology})  —  ${slots.length} disco(s)  —  ${arr.total_gb || '?'}GB  —  ${arr.status}`);
      if (slots.length > 0) lines.push(`    └─ Miembros: ${slots.join(', ')}`);
    });
    lines.push('');
  }

  if (vm.type) {
    lines.push(`── GESTIÓN DE VOLÚMENES: ${vm.type} ──`);
    if (vm.type === 'LVM') {
      if (vm.lvm_pvs) lines.push(`  PV: ${vm.lvm_pvs.replace(/\n/g, ', ')}`);
      if (vm.lvm_vgs) lines.push(`  VG: ${vm.lvm_vgs.replace(/\n/g, ', ')}`);
      if (vm.lvm_lvs) lines.push(`  LV: ${vm.lvm_lvs.replace(/\n/g, ', ')}`);
    } else if (vm.type === 'ZFS') {
      if (vm.zfs_pool) lines.push(`  Pool: ${vm.zfs_pool}${vm.zfs_total_gb ? `  —  ${vm.zfs_total_gb}GB` : ''}${vm.zfs_status ? `  —  ${vm.zfs_status}` : ''}`);
    } else if (vm.type === 'StorageSpaces') {
      if (vm.ss_pool) lines.push(`  Pool: ${vm.ss_pool}`);
    }
    lines.push('');
  }

  if (vols.length > 0) {
    lines.push('── VOLÚMENES LÓGICOS ──');
    vols.forEach(vol => {
      const parts = [
        vol.device,
        vol.origin && `← ${vol.origin}`,
        vol.size_gb && `${vol.size_gb}GB`,
        vol.filesystem,
        vol.mount_point && `→ ${vol.mount_point}`,
        vol.usage && `(${vol.usage})`,
        vol.immutable && '🔒',
        vol.encrypted && `🔐${vol.encryption_type ? ` ${vol.encryption_type}` : ''}`,
      ].filter(Boolean);
      lines.push(`  ${parts.join('  ')}`);
    });
  }

  return lines.length > 0 ? lines.join('\n') : '';
}

// ── Main component ────────────────────────────────────────────────────────────

// ── CopyToServerModal ────────────────────────────────────────────────────────
function CopyToServerModal({ disk, currentServerId, companyId, onClose }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { ok: true } | { error: '...' }

  useEffect(() => {
    api.getServers(companyId)
      .then(res => {
        const list = Array.isArray(res) ? res : (res.data || res.servers || []);
        setServers(list.filter(s => String(s.id) !== String(currentServerId)));
      })
      .catch(() => setServers([]))
      .finally(() => setLoading(false));
  }, [companyId, currentServerId]);

  const filtered = servers.filter(s =>
    (s.name || s.hostname || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.hostname || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    setResult(null);
    try {
      const existing = await api.getServerStorage(selected.id, companyId);
      const base = existing?.data?.data || {};
      const existingDisks = Array.isArray(base.physical_disks) ? base.physical_disks : [];
      const clone = { ...disk, slot: '', serial: '', health: { ...(disk.health || {}), smart_status: 'OK', power_on_hours: '', temperature_c: '', reallocated_sectors: '', wear_level_pct: '', checked_at: '' } };
      await api.updateServerStorage(selected.id, {
        inventory_date: existing?.data?.inventory_date || '',
        taken_by: existing?.data?.taken_by || '',
        data: { ...base, physical_disks: [...existingDisks, clone] },
      }, companyId);
      setResult({ ok: true, name: selected.name || selected.hostname });
    } catch (err) {
      setResult({ error: err.message || 'Error al copiar' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sdp-copy-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sdp-copy-shell">
        {/* Header */}
        <div className="sdp-copy-header">
          <div className="sdp-copy-header-left">
            <ArrowTopRightOnSquareIcon className="sdp-copy-header-icon" />
            <h4 className="sdp-copy-header-title">Copiar disco a otro servidor</h4>
          </div>
          <button type="button" onClick={onClose} className="sdp-copy-close-btn">
            <XMarkIcon />
          </button>
        </div>

        {/* Disk summary */}
        <div className="sdp-copy-disk-summary">
          <strong>{disk.manufacturer} {disk.model}</strong>{' '}
          {disk.capacity_gb ? `· ${disk.capacity_gb} GB` : ''}{' '}
          {disk.type ? `· ${disk.type}` : ''}
          <div className="sdp-copy-disk-summary-hint">Se copiará sin slot ni número de serie</div>
        </div>

        {result?.ok ? (
          <div className="sdp-copy-success-wrap">
            <CheckIcon className="sdp-copy-success-icon" />
            <div className="sdp-copy-success-text">Disco copiado correctamente</div>
            <div className="sdp-copy-success-sub">Añadido al inventario de <strong>{result.name}</strong></div>
            <button type="button" onClick={onClose} className="sdp-copy-close-success">
              Cerrar
            </button>
          </div>
        ) : (
          <>
            {/* Search */}
            <input
              className="form-control form-control-sm"
              placeholder="Buscar servidor..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginBottom: '0.65rem' }}
              autoFocus
            />

            {/* Server list */}
            <div className="sdp-copy-server-list">
              {loading ? (
                <div className="sdp-copy-loading">Cargando servidores...</div>
              ) : filtered.length === 0 ? (
                <div className="sdp-copy-empty">No se encontraron servidores</div>
              ) : filtered.map(s => (
                <div key={s.id}
                  onClick={() => setSelected(s)}
                  className={`sdp-copy-server-row${selected?.id === s.id ? ' is-selected' : ''}`}>
                  <span>{s.name || s.hostname || `Servidor #${s.id}`}</span>
                  {s.hostname && s.name && s.name !== s.hostname && (
                    <span className="sdp-copy-server-hostname">{s.hostname}</span>
                  )}
                  {selected?.id === s.id && (
                    <CheckIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                  )}
                </div>
              ))}
            </div>

            {result?.error && (
              <div className="sdp-copy-error">
                {result.error}
              </div>
            )}

            {/* Footer */}
            <div className="sdp-copy-footer">
              <button type="button" onClick={onClose} className="sdp-copy-cancel-btn">
                Cancelar
              </button>
              <button type="button" onClick={handleConfirm} disabled={!selected || saving}
                className={`sdp-copy-confirm-btn${saving ? ' is-saving' : ''}`}>
                {saving ? 'Copiando...' : 'Copiar disco'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ServerDisksPanel({ server, companyId, onClose, getStorage = null, updateStorage = null, variant = 'classic' }) {
  const isCards = variant === 'cards';
  const tabsVisibles = isCards ? [{ key: 'summary', label: 'General' }, ...TABS] : TABS;
  const [activeTab, setActiveTab] = useState(isCards ? 'summary' : 'meta');
  const [meta, setMeta] = useState({ inventory_date: '', taken_by: '' });
  const [data, setData] = useState(EMPTY_DATA());
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storageAdvisory, setStorageAdvisory] = useState(null);
  const [advisoryDismissed, setAdvisoryDismissed] = useState(false);
  const [savedDiskCount, setSavedDiskCount] = useState(0);
  const [diskModal, setDiskModal] = useState({ open: false, disk: null, idx: null });
  const [raidHwModal, setRaidHwModal] = useState({ open: false, arr: null, idx: null });
  const [copyToModal, setCopyToModal] = useState({ open: false, disk: null });

  const openDiskModal = (disk, idx) => setDiskModal({ open: true, disk, idx });
  const closeDiskModal = () => setDiskModal({ open: false, disk: null, idx: null });
  const saveDiskModal = (updatedDisk) => {
    const arr = [...data.physical_disks];
    if (diskModal.idx === null) arr.push(updatedDisk);
    else arr[diskModal.idx] = updatedDisk;
    setSection('physical_disks', arr);
    closeDiskModal();
  };

  const openRaidHwModal = (arr, idx) => setRaidHwModal({ open: true, arr, idx });
  const closeRaidHwModal = () => setRaidHwModal({ open: false, arr: null, idx: null });
  const saveRaidHwModal = (updatedArr) => {
    const arr = [...data.raid_hw];
    if (raidHwModal.idx === null) arr.push(updatedArr);
    else arr[raidHwModal.idx] = updatedArr;
    setSection('raid_hw', arr);
    closeRaidHwModal();
  };

  // Load existing record
  useEffect(() => {
    if (!server?.id) { setLoading(false); return; }
    (getStorage || api.getServerStorage.bind(api))(server.id, companyId)
      .then(res => {
        if (res.data) {
          setMeta({ inventory_date: res.data.inventory_date || '', taken_by: res.data.taken_by || '' });
          const loaded = {
            ...EMPTY_DATA(),
            ...res.data.data,
            physical_disks: Array.isArray(res.data?.data?.physical_disks)
              ? res.data.data.physical_disks.map(normalizeDiskForUi)
              : [],
          };
          // Normalize RAID disk_slots: replace serials with slot names
          const disks = loaded.physical_disks;
          if (Array.isArray(loaded.raid_hw)) {
            loaded.raid_hw = loaded.raid_hw.map(r => ({
              ...r,
              disk_slots: normalizeDiskSlots(r.disk_slots, disks),
              hot_spare_slots: normalizeDiskSlots(r.hot_spare_slots, disks),
            }));
          }
          if (Array.isArray(loaded.raid_sw)) {
            loaded.raid_sw = loaded.raid_sw.map(r => ({
              ...r,
              disk_slots: normalizeDiskSlots(r.disk_slots, disks),
            }));
          }
          setData(loaded);
          setSavedDiskCount(Array.isArray(loaded.physical_disks) ? loaded.physical_disks.length : 0);
          setStorageAdvisory(getStorageAdvisory(res.data));
        }
      })
      .catch(() => {
        setStorageAdvisory(null);
      })
      .finally(() => setLoading(false));
  }, [server]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  // El aviso de almacenamiento se puede ocultar mientras se revisa el inventario;
  // al recargar datos vuelve a mostrarse.
  useEffect(() => { setAdvisoryDismissed(false); }, [storageAdvisory]);

  const setSection = useCallback((section, value) => {
    setData(prev => ({ ...prev, [section]: value }));
    markDirty();
  }, [markDirty]);

  const setVm = useCallback((field, value) => {
    setData(prev => ({ ...prev, volume_manager: { ...prev.volume_manager, [field]: value } }));
    markDirty();
  }, [markDirty]);

  const setObs = useCallback((field, value) => {
    setData(prev => ({ ...prev, observations: { ...prev.observations, [field]: value } }));
    markDirty();
  }, [markDirty]);

  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      await (updateStorage || api.updateServerStorage.bind(api))(server.id, {
        inventory_date: meta.inventory_date,
        taken_by: meta.taken_by,
        data,
      }, companyId);
      setSavedDiskCount(data.physical_disks.length);
      setIsDirty(false);
    } catch (err) {
      setApiError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const diskCount = data.physical_disks.length;
  const vm = data.volume_manager;
  const obs = data.observations;

  // Sources for logical volume "origin" field: RAID arrays + VGs + pools
  const volumeSources = useMemo(() => {
    const sources = [];
    data.raid_hw.forEach(r => {
      if (r.array_id) sources.push({ value: r.array_id, label: `RAID HW: ${r.array_id} (${r.raid_type})` });
    });
    data.raid_sw.forEach(r => {
      if (r.name) sources.push({ value: r.name, label: `RAID SW: ${r.name} (${r.raid_type}, ${r.technology})` });
    });
    if (vm.type === 'LVM' && vm.lvm_vgs) {
      vm.lvm_vgs.split(/[\n,]/).map(s => s.trim()).filter(Boolean).forEach(vg => {
        const vgName = vg.split(/\s+/)[0];
        if (vgName) sources.push({ value: vgName, label: `LVM VG: ${vgName}` });
      });
    }
    if (vm.type === 'ZFS' && vm.zfs_pool) {
      sources.push({ value: vm.zfs_pool, label: `ZFS Pool: ${vm.zfs_pool}` });
    }
    if (vm.type === 'StorageSpaces' && vm.ss_pool) {
      sources.push({ value: vm.ss_pool, label: `SS Pool: ${vm.ss_pool}` });
    }
    data.physical_disks.filter(d => d.role === 'JBOD' || d.role === 'Standalone').forEach(d => {
      if (d.slot) sources.push({ value: d.slot, label: `Disco directo: ${d.slot}${d.model ? ` (${d.model})` : ''}` });
    });
    return sources;
  }, [data]);

  const autoLayerMap = useMemo(() => generateLayerMap(data), [data]);

  if (loading) return (
    <div className="dv-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: 'var(--text-muted)' }}>Cargando inventario de almacenamiento…</span>
    </div>
  );

  return (
    <div className={`dv-overlay${isCards ? ' sf2-diskpanel' : ''}`}>
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => onClose(savedDiskCount)}>
          <ArrowLeftIcon /> {server?.name || 'Servidor'}
        </button>
        <DetailCrumb label={`${server?.name || ''} · Inventario de Discos`} />
        <div className="dv-topbar-spacer" />

        {apiError && (
          <span style={{ color: 'var(--sdp-fail)', fontSize: '0.78rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isDirty && (
            <button className="dv-btn dv-btn-secondary" type="button"
              onClick={() => { setIsDirty(false); setApiError(null); }} disabled={saving}>
              <XMarkIcon /> Descartar
            </button>
          )}
          <button className="dv-btn dv-btn-primary" type="button"
            onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* ── SmartBar ── */}
      {/* ── SmartBar (solo ficha clásica: en tarjetas los contadores van a chips) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('disks')}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>💾</span>
          <span className="dv-smart-btn-value" style={{ color: 'var(--accent)' }}>{diskCount}</span>
          <span className="dv-smart-btn-label">Discos físicos</span>
        </button>
        {data.raid_hw.length > 0 && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('raid_hw')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>🧱</span>
            <span className="dv-smart-btn-value" style={{ color: 'var(--accent-2)' }}>{data.raid_hw.length}</span>
            <span className="dv-smart-btn-label">Arrays HW</span>
          </button>
        )}
        {data.raid_sw.length > 0 && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('raid_sw')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>⚙️</span>
            <span className="dv-smart-btn-value" style={{ color: 'var(--sdp-sky)' }}>{data.raid_sw.length}</span>
            <span className="dv-smart-btn-label">Arrays SW</span>
          </button>
        )}
        {data.logical_volumes.length > 0 && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('volumes')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>📂</span>
            <span className="dv-smart-btn-value" style={{ color: 'var(--sdp-ok)' }}>{data.logical_volumes.length}</span>
            <span className="dv-smart-btn-label">Volúmenes</span>
          </button>
        )}
        {(obs.failed_disks || obs.degraded_arrays || obs.risks) && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('observations')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>⚠️</span>
            <span className="dv-smart-btn-value" style={{ color: 'var(--sdp-fail)', fontSize: '0.78rem' }}>Alertas</span>
          </button>
        )}
        {meta.inventory_date && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('meta')}>
            <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>🗓️</span>
            <span className="dv-smart-btn-value" style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{meta.inventory_date}</span>
          </button>
        )}
      </div>
      )}

      {storageAdvisory && !advisoryDismissed && (
        <div className="sdp-advisory">
          <button
            type="button"
            className="sdp-advisory-dismiss"
            onClick={() => setAdvisoryDismissed(true)}
            title="Ocultar este aviso mientras revisas el inventario"
          >
            Omitir
          </button>
          <div className="sdp-advisory-label">
            {getStorageAdvisoryEyebrow(storageAdvisory)}
          </div>
          <div className="sdp-advisory-title">{storageAdvisory.title}</div>
          <div className="sdp-advisory-body">{storageAdvisory.message}</div>
          {Array.isArray(storageAdvisory.instructions) && storageAdvisory.instructions.length > 0 && (
            <div className="sdp-advisory-steps">
              {storageAdvisory.instructions.map((step, index) => (
                <div key={`${index}-${step}`}>{index + 1}. {step}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body" style={{ overflowY: isCards ? 'hidden' : 'auto' }}>
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`} style={{ maxWidth: '100%' }}>
          <div className={isCards ? 'sf2-headarea' : 'sf2-plain'}>
            {isCards ? (
              <>
              <div className="sf2-head">
                <div className="sf2-head-title">
                  <div className="sf2-title-row">
                    <h1>Inventario de almacenamiento</h1>
                  </div>
                  <div className="sf2-crumb">
                    {server?.name || 'Servidor'} · {diskCount} discos
                    {meta.inventory_date ? ` · ${meta.inventory_date}` : ''}
                  </div>
                </div>
              </div>
                <div className="sf2-chips">
                  <button type="button" className="sf2-chip" onClick={() => setActiveTab('disks')}>
                    <b>{diskCount}</b><span>discos físicos</span>
                  </button>
                  {data.raid_hw.length > 0 && (
                    <button type="button" className="sf2-chip" onClick={() => setActiveTab('raid_hw')}>
                      <b>{data.raid_hw.length}</b><span>arrays HW</span>
                    </button>
                  )}
                  {data.raid_sw.length > 0 && (
                    <button type="button" className="sf2-chip" onClick={() => setActiveTab('raid_sw')}>
                      <b>{data.raid_sw.length}</b><span>arrays SW</span>
                    </button>
                  )}
                  {data.logical_volumes.length > 0 && (
                    <button type="button" className="sf2-chip" onClick={() => setActiveTab('volumes')}>
                      <b>{data.logical_volumes.length}</b><span>volúmenes</span>
                    </button>
                  )}
                  {(obs.failed_disks || obs.degraded_arrays || obs.pool_errors || obs.risks) && (
                    <button type="button" className="sf2-chip" onClick={() => setActiveTab('observations')}>
                      <b>⚠</b><span>alertas</span>
                    </button>
                  )}
                </div>
              </>
            ) : (
              <h2 className="dv-record-title" style={{ pointerEvents: 'none' }}>
                🗄️ {server?.name} — Inventario de Almacenamiento
              </h2>
            )}

            {/* Tabs nav */}
            <div className="dv-tabs-nav">
            {tabsVisibles.map(tab => (
              <button key={tab.key} type="button"
                className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>
          </div>

          <div className={isCards ? 'sf2-scroll' : 'sf2-plain'}>

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && activeTab === 'summary' && (() => {
            const slotCount = data.slot_count ?? 8;
            const ocupados = data.physical_disks.filter(
              d => (d.location_type || 'bay') === 'bay' && d.slot && d.role !== 'Retirado'
            ).length;
            const libres = Math.max(0, slotCount - ocupados);
            const alertas = [
              obs.failed_disks && { label: 'Discos en mal estado', text: obs.failed_disks },
              obs.degraded_arrays && { label: 'Arrays degradados', text: obs.degraded_arrays },
              obs.pool_errors && { label: 'Pools con errores', text: obs.pool_errors },
              obs.special_configs && { label: 'Configuraciones especiales', text: obs.special_configs },
              obs.risks && { label: 'Riesgos detectados', text: obs.risks },
            ].filter(Boolean);
            return (
              <div className="dv-tab-pane">
                <div className="dv-section">
                  <h3 className="dv-section-title">Resumen del almacenamiento</h3>
                  <div className="sf2-hl">
                    <div className="sf2-kpi"><span>Discos físicos</span><b>{diskCount}</b></div>
                    <div className="sf2-kpi"><span>Slots ocupados</span><b>{ocupados} <small style={{ fontWeight: 400, color: 'var(--text-muted)' }}>/ {slotCount}</small></b></div>
                    <div className="sf2-kpi"><span>Slots libres</span><b>{libres}</b></div>
                    <div className="sf2-kpi"><span>Arrays HW</span><b>{data.raid_hw.length}</b></div>
                    <div className="sf2-kpi"><span>Arrays SW</span><b>{data.raid_sw.length}</b></div>
                    <div className="sf2-kpi"><span>Volúmenes lógicos</span><b>{data.logical_volumes.length}</b></div>
                  </div>
                </div>

                <div className="dv-section">
                  <h3 className="dv-section-title">Alertas y observaciones</h3>
                  {alertas.length === 0 ? (
                    <div className="sf2-empty">Sin alertas registradas en este inventario.</div>
                  ) : (
                    <div className="sf2-rows">
                      {alertas.map((a) => (
                        <div key={a.label} className="sf2-row">
                          <div className="sf2-row-main">
                            <div><b>{a.label}</b></div>
                            <div className="sf2-hint">{a.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="sf2-link" onClick={() => setActiveTab('observations')}>
                      Editar observaciones →
                    </button>
                  </div>
                </div>

                <div className="dv-section">
                  <h3 className="dv-section-title">Datos del inventario</h3>
                  <div className="sf2-fields" style={{ '--n': 3 }}>
                    <div className="sf2-field"><label>Fecha</label><div className="v">{meta.inventory_date || '—'}</div></div>
                    <div className="sf2-field"><label>Realizado por</label><div className="v">{meta.taken_by || '—'}</div></div>
                    <div className="sf2-field"><label>Slots del servidor</label><div className="v">{slotCount}</div></div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="sf2-link" onClick={() => setActiveTab('meta')}>
                      Editar metadatos →
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Tab: Metadatos ── */}
          {activeTab === 'meta' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Metadatos del inventario</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Fecha de toma del inventario</label>
                    <input className="form-control" type="date" value={meta.inventory_date}
                      onChange={e => { setMeta(p => ({ ...p, inventory_date: e.target.value })); markDirty(); }} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Realizado por</label>
                    <input className="form-control" value={meta.taken_by}
                      onChange={e => { setMeta(p => ({ ...p, taken_by: e.target.value })); markDirty(); }}
                      placeholder="Nombre del técnico o sistema" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Slots de disco del servidor</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input className="form-control" type="number" min="1" max="128" style={{ width: 90 }}
                        value={data.slot_count ?? 8}
                        onChange={e => { setSection('slot_count', Math.max(1, parseInt(e.target.value) || 1)); markDirty(); }} />
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        slots (0 – {(data.slot_count ?? 8) - 1})
                      </span>
                    </div>
                    <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: 3 }}>Numerados desde 0</div>
                  </div>
                </div>
                <div className="mt-3" style={{ background: 'var(--sdp-info-bg)', border: '1px solid var(--sdp-info-bd)', borderRadius: 6, padding: '0.75rem', fontSize: '0.82rem', color: 'var(--sdp-info)' }}>
                  <strong>Nota:</strong> Este inventario es un dato puntual registrado manualmente.
                  En fases posteriores el CMDB podrá actualizarlo automáticamente vía SSH, WMI o iDRAC/iLO.
                  El almacenamiento en red (SAN, NAS, iSCSI) se documenta por separado.
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Controladoras ── */}
          {activeTab === 'controllers' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="dv-section-title" style={{ margin: 0 }}>Controladoras de almacenamiento</h3>
                  <button type="button" className="dv-btn dv-btn-secondary"
                    onClick={() => { setSection('controllers', [...data.controllers, EMPTY_CONTROLLER()]); }}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Añadir controladora
                  </button>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Solo rellenar si hay controladora hardware (HP SmartArray, Dell PERC, LSI MegaRAID, etc.).
                  En servidores con RAID software o NVMe directo, dejar vacío.
                </p>
                {data.controllers.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                    Sin controladoras hardware registradas. El almacenamiento va por software.
                  </div>
                )}
                {data.controllers.map((ctrl, i) => (
                  <ControllerRow key={i} ctrl={ctrl}
                    onChange={v => { const arr = [...data.controllers]; arr[i] = v; setSection('controllers', arr); }}
                    onRemove={() => setSection('controllers', data.controllers.filter((_, idx) => idx !== i))} />
                ))}
              </div>
            </div>
          )}

          {/* ── Tab: Discos físicos ── */}
          {activeTab === 'disks' && (() => {
            const slotCount = data.slot_count ?? 8;
            const bayDiskList = data.physical_disks.filter(d => (d.location_type || 'bay') === 'bay');
            const internalDiskList = data.physical_disks.filter(d => d.location_type === 'internal');
            const occupiedSlots = new Map(
              bayDiskList
                .filter(d => d.role !== 'Retirado' && d.slot)
                .map(d => [canonicalBaySlot(d.slot), d])
            );
            const allSlots = Array.from({ length: slotCount }, (_, i) => `Slot ${i}`);
            const freeCount = allSlots.filter(s => !occupiedSlots.has(s)).length;
            return (
              <div className="dv-tab-pane">
                <div className="dv-section sdp-storage-section">
                  <div className="sdp-section-toolbar">
                    <div>
                      <h3 className="dv-section-title sdp-section-title-inline">Bahía de discos</h3>
                      <div className="sdp-slot-summary">
                        <span>{slotCount} slots</span>
                        <span className="sdp-slot-summary-divider">·</span>
                        <span className="sdp-slot-summary-occupied">{slotCount - freeCount} ocupados</span>
                        <span className="sdp-slot-summary-divider">·</span>
                        <span className="sdp-slot-summary-free">{freeCount} libres</span>
                      </div>
                    </div>
                    <div className="sdp-toolbar-actions">
                      <button type="button" className="dv-btn dv-btn-secondary"
                        onClick={() => openDiskModal(EMPTY_DISK('internal'), null)}>
                        <PlusCircleIcon style={{ width: 15, height: 15 }} /> Disco interno
                      </button>
                      <button type="button" className="dv-btn dv-btn-secondary"
                        onClick={() => openDiskModal(EMPTY_DISK('bay'), null)}>
                        <PlusCircleIcon style={{ width: 16, height: 16 }} /> Instalar disco
                      </button>
                    </div>
                  </div>
                  <div className="sdp-slot-grid">
                    {allSlots.map(slotLabel => {
                      const disk = occupiedSlots.get(slotLabel);
                      const idx = disk ? data.physical_disks.indexOf(disk) : -1;
                      const smart = disk?.health?.smart_status;
                      const slotTone = !disk ? 'empty' : smart === 'Failed' ? 'failed' : smart === 'Warning' ? 'warning' : 'ok';
                      return (
                        <button key={slotLabel} type="button"
                          onClick={() => disk && openDiskModal(disk, idx)}
                          title={disk ? `${slotLabel}: ${[disk.manufacturer, disk.model].filter(Boolean).join(' ')} ${disk.capacity_gb ? '· ' + fmtCap(disk.capacity_gb) : ''}` : `${slotLabel}: libre`}
                          className={`sdp-slot-tile sdp-slot-tile-${slotTone}${disk ? ' is-clickable' : ''}`}>
                          <span className="sdp-slot-tile-label">{slotLabel.replace('Slot ', 'S')}</span>
                          <span className="sdp-slot-tile-dot" />
                          {disk && <span className="sdp-slot-tile-capacity">{fmtCap(disk.capacity_gb)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="dv-section sdp-storage-section">
                  <h3 className="dv-section-title sdp-section-title-inline">Discos en bahía</h3>
                  {bayDiskList.filter(d => d.role !== 'Retirado').length === 0 ? (
                    <div className="sdp-empty-state">
                      <CpuChipIcon className="sdp-empty-state-icon" />
                      No hay discos en bahía. Pulsa «Instalar disco» para comenzar.
                    </div>
                  ) : (
                    <div className="sdp-card-grid">
                      {[...bayDiskList]
                        .map((disk, i) => ({ disk, i: data.physical_disks.indexOf(disk) }))
                        .filter(({ disk }) => disk.role !== 'Retirado')
                        .sort((a, b) => slotNum(a.disk.slot) - slotNum(b.disk.slot) || (a.disk.slot || '').localeCompare(b.disk.slot || ''))
                        .map(({ disk, i }) => (
                          <DiskKanbanCard key={i} disk={disk}
                            onEdit={() => openDiskModal(disk, i)}
                            onCopy={() => {
                              const clone = { ...disk, slot: '', serial: '', location_type: disk.location_type || 'bay', health: { ...disk.health } };
                              setSection('physical_disks', [...data.physical_disks, clone]);
                            }}
                            onCopyTo={() => setCopyToModal({ open: true, disk })}
                            onRetire={() => {
                              // eslint-disable-next-line no-restricted-globals
                              if (window.confirm(`¿Retirar el disco del ${disk.slot || 'este slot'}? Quedará en el histórico como "Retirado".`)) {
                                const arr = [...data.physical_disks];
                                arr[i] = { ...disk, role: 'Retirado', slot: '' };
                                setSection('physical_disks', arr);
                              }
                            }}
                            onRemove={() => {
                              // eslint-disable-next-line no-restricted-globals
                              if (window.confirm('¿Eliminar este disco del inventario por completo?')) {
                                setSection('physical_disks', data.physical_disks.filter((_, idx) => idx !== i));
                              }
                            }} />
                        ))}
                    </div>
                  )}
                </div>

                {(internalDiskList.filter(d => d.role !== 'Retirado').length > 0 || true) && (
                  <div className="dv-section sdp-storage-section">
                    <div className="sdp-section-toolbar sdp-section-toolbar-tight">
                      <div>
                        <h3 className="dv-section-title sdp-section-title-inline">Discos internos (onboard)</h3>
                        <div className="sdp-section-subtitle">M.2, DOM, NVMe de placa — no ocupan slot de bahía</div>
                      </div>
                    </div>
                    {internalDiskList.filter(d => d.role !== 'Retirado').length === 0 ? (
                      <div className="sdp-empty-state sdp-empty-state-compact">
                        Sin discos internos. Pulsa «Disco interno» para añadir un NVMe de OS, DOM, etc.
                      </div>
                    ) : (
                      <div className="sdp-card-grid">
                        {internalDiskList
                          .map((disk) => ({ disk, i: data.physical_disks.indexOf(disk) }))
                          .filter(({ disk }) => disk.role !== 'Retirado')
                          .map(({ disk, i }) => (
                            <DiskKanbanCard key={i} disk={disk}
                              onEdit={() => openDiskModal(disk, i)}
                              onCopy={() => {
                                const clone = { ...disk, slot: '', serial: '', location_type: disk.location_type || 'internal', health: { ...disk.health } };
                                setSection('physical_disks', [...data.physical_disks, clone]);
                              }}
                              onCopyTo={() => setCopyToModal({ open: true, disk })}
                              onRemove={() => {
                                // eslint-disable-next-line no-restricted-globals
                                if (window.confirm('¿Eliminar este disco interno del inventario?')) {
                                  setSection('physical_disks', data.physical_disks.filter((_, idx) => idx !== i));
                                }
                              }} />
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Retired disks */}
                {data.physical_disks.some(d => d.role === 'Retirado') && (
                  <div className="dv-section" style={{ marginTop: '0.5rem', opacity: 0.6 }}>
                    <h3 className="dv-section-title" style={{ marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Discos retirados (histórico)</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.85rem' }}>
                      {data.physical_disks.filter(d => d.role === 'Retirado').map((disk) => {
                        const realIdx = data.physical_disks.indexOf(disk);
                        return (
                          <DiskKanbanCard key={realIdx} disk={disk}
                            onEdit={() => openDiskModal(disk, realIdx)}
                            onCopy={() => {
                              const clone = { ...disk, slot: '', serial: '', location_type: disk.location_type || 'bay', health: { ...disk.health } };
                              setSection('physical_disks', [...data.physical_disks, clone]);
                            }}
                            onCopyTo={() => setCopyToModal({ open: true, disk })}
                            onRemove={() => setSection('physical_disks', data.physical_disks.filter((_, idx) => idx !== realIdx))} />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* ── Disk edit modal ── */}
          {diskModal.open && (
            <DiskEditModal
              disk={diskModal.disk}
              onSave={saveDiskModal}
              onClose={closeDiskModal}
              availableSlots={diskModal.disk?.location_type === 'internal' ? [] : (() => {
                const slotCount = data.slot_count ?? 8;
                const allSlots = Array.from({ length: slotCount }, (_, i) => `Slot ${i}`);
                const occupiedSlots = new Set(
                  data.physical_disks
                    .filter(d => d.role !== 'Retirado' && (d.location_type || 'bay') === 'bay')
                    .map(d => canonicalBaySlot(d.slot))
                    .filter(Boolean)
                );
                return allSlots.map(s => ({ label: s, occupied: occupiedSlots.has(s) }));
              })()}
            />
          )}

          {raidHwModal.open && (
            <RaidHwEditModal
              arr={raidHwModal.arr}
              controllers={data.controllers}
              physicalDisks={data.physical_disks}
              busySlots={[
                ...data.raid_hw
                  .filter((_, idx) => idx !== raidHwModal.idx)
                  .flatMap(r => r.disk_slots || []),
                ...data.raid_sw.flatMap(r => r.disk_slots || []),
              ]}
              onSave={saveRaidHwModal}
              onClose={closeRaidHwModal}
            />
          )}

          {copyToModal.open && (
            <CopyToServerModal
              disk={copyToModal.disk}
              currentServerId={server?.id}
              companyId={companyId}
              onClose={() => setCopyToModal({ open: false, disk: null })}
            />
          )}

          {/* ── Tab: RAID Hardware ── */}
          {activeTab === 'raid_hw' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="dv-section-title" style={{ margin: 0 }}>Arrays RAID hardware</h3>
                  <button type="button" className="dv-btn dv-btn-secondary"
                    onClick={() => openRaidHwModal(EMPTY_RAID_HW(), null)}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Añadir array
                  </button>
                </div>
                {data.raid_hw.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                    Sin arrays RAID hardware. Pasar a RAID software si procede.
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem' }}>
                  {data.raid_hw.map((arr, i) => (
                    <RaidHwKanbanCard
                      key={i}
                      idx={i}
                      arr={arr}
                      controllers={data.controllers}
                      onEdit={() => openRaidHwModal(arr, i)}
                      onRemove={() => setSection('raid_hw', data.raid_hw.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: RAID Software ── */}
          {activeTab === 'raid_sw' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="dv-section-title" style={{ margin: 0 }}>Arrays RAID software</h3>
                  <button type="button" className="dv-btn dv-btn-secondary"
                    onClick={() => setSection('raid_sw', [...data.raid_sw, EMPTY_RAID_SW()])}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Añadir array
                  </button>
                </div>
                {data.raid_sw.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                    Sin arrays RAID software (mdadm, Storage Spaces, etc.).
                  </div>
                )}
                {data.raid_sw.map((arr, i) => (
                  <RaidSwRow key={i} arr={arr} physicalDisks={data.physical_disks}
                    busySlots={[
                      ...data.raid_hw.flatMap(r => r.disk_slots || []),
                      ...data.raid_sw.filter((_, idx) => idx !== i).flatMap(r => r.disk_slots || []),
                    ]}
                    onChange={v => { const a = [...data.raid_sw]; a[i] = v; setSection('raid_sw', a); }}
                    onRemove={() => setSection('raid_sw', data.raid_sw.filter((_, idx) => idx !== i))} />
                ))}
              </div>
            </div>
          )}

          {/* ── Tab: Gestión de volúmenes ── */}
          {activeTab === 'volmgr' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Tecnología de gestión de volúmenes</h3>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Tecnología activa</label>
                    <select className="form-select" value={vm.type || ''}
                      onChange={e => setVm('type', e.target.value || null)}>
                      <option value="">-- Ninguna / No aplica --</option>
                      <option value="LVM">LVM (Linux)</option>
                      <option value="ZFS">ZFS</option>
                      <option value="StorageSpaces">Storage Spaces (Windows)</option>
                    </select>
                  </div>
                </div>
              </div>

              {vm.type === 'LVM' && (
                <div className="dv-section">
                  <h3 className="dv-section-title">LVM</h3>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">Physical Volumes (PV)</label>
                      <textarea className="form-control" rows={2} value={vm.lvm_pvs}
                        onChange={e => setVm('lvm_pvs', e.target.value)}
                        placeholder="/dev/sda — 1 TB — allocatable&#10;/dev/sdb — 1 TB — allocatable" />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Volume Groups (VG)</label>
                      <textarea className="form-control" rows={2} value={vm.lvm_vgs}
                        onChange={e => setVm('lvm_vgs', e.target.value)}
                        placeholder="vg_data — 2 TB — 1.5 TB libres" />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Logical Volumes (LV)</label>
                      <textarea className="form-control" rows={3} value={vm.lvm_lvs}
                        onChange={e => setVm('lvm_lvs', e.target.value)}
                        placeholder="lv_root — 50 GB — activo&#10;lv_data — 1 TB — activo" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Estado general</label>
                      <input className="form-control" value={vm.lvm_status}
                        onChange={e => setVm('lvm_status', e.target.value)} placeholder="OK" />
                    </div>
                  </div>
                </div>
              )}

              {vm.type === 'ZFS' && (
                <div className="dv-section">
                  <h3 className="dv-section-title">ZFS</h3>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Nombre del pool</label>
                      <input className="form-control" value={vm.zfs_pool}
                        onChange={e => setVm('zfs_pool', e.target.value)} placeholder="tank" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo de vdevs</label>
                      <input className="form-control" value={vm.zfs_vdevs}
                        onChange={e => setVm('zfs_vdevs', e.target.value)} placeholder="mirror / raidz2" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Discos por vdev</label>
                      <input className="form-control" value={vm.zfs_disks_per_vdev}
                        onChange={e => setVm('zfs_disks_per_vdev', e.target.value)} placeholder="2" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tamaño total (GB)</label>
                      <input className="form-control" type="number" min="0" value={vm.zfs_total_gb}
                        onChange={e => setVm('zfs_total_gb', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Estado</label>
                      <input className="form-control" value={vm.zfs_status}
                        onChange={e => setVm('zfs_status', e.target.value)} placeholder="ONLINE" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Snapshots</label>
                      <input className="form-control" value={vm.zfs_snapshots}
                        onChange={e => setVm('zfs_snapshots', e.target.value)} placeholder="tank@backup-2026-01 ..." />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Datasets inmutables o protegidos</label>
                      <input className="form-control" value={vm.zfs_protected}
                        onChange={e => setVm('zfs_protected', e.target.value)} placeholder="tank/backups (readonly=on)" />
                    </div>
                  </div>
                </div>
              )}

              {vm.type === 'StorageSpaces' && (
                <div className="dv-section">
                  <h3 className="dv-section-title">Storage Spaces (Windows)</h3>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Nombre del pool</label>
                      <input className="form-control" value={vm.ss_pool}
                        onChange={e => setVm('ss_pool', e.target.value)} placeholder="StoragePool1" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo de resiliencia</label>
                      <select className="form-select" value={vm.ss_resilience}
                        onChange={e => setVm('ss_resilience', e.target.value)}>
                        <option value="">-- Seleccionar --</option>
                        <option>Simple (sin resiliencia)</option>
                        <option>Mirror (2 copias)</option>
                        <option>Mirror (3 copias)</option>
                        <option>Parity</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Estado</label>
                      <input className="form-control" value={vm.ss_status}
                        onChange={e => setVm('ss_status', e.target.value)} placeholder="OK" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Discos físicos del pool</label>
                      <input className="form-control" value={vm.ss_disks}
                        onChange={e => setVm('ss_disks', e.target.value)} placeholder="PhysicalDisk1, PhysicalDisk2" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Volúmenes creados</label>
                      <input className="form-control" value={vm.ss_volumes}
                        onChange={e => setVm('ss_volumes', e.target.value)} placeholder="Vol1 (2 TB), Vol2 (500 GB)" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Volúmenes lógicos ── */}
          {activeTab === 'volumes' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="dv-section-title" style={{ margin: 0 }}>Volúmenes lógicos presentados al SO</h3>
                  <button type="button" className="dv-btn dv-btn-secondary"
                    onClick={() => setSection('logical_volumes', [...data.logical_volumes, EMPTY_VOLUME()])}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Añadir volumen
                  </button>
                </div>
                <p className="dv-hint" style={{ marginTop: 0 }}>
                  Estos volúmenes se reflejan automáticamente en la pestaña
                  <strong> Almacenamiento</strong> del servidor, donde puedes enlazarlos con su LUN.
                </p>
                {data.logical_volumes.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                    No hay volúmenes lógicos registrados.
                  </div>
                )}
                {data.logical_volumes.map((vol, i) => (
                  <VolumeRow key={i} vol={vol} idx={i} sources={volumeSources}
                    onChange={v => { const a = [...data.logical_volumes]; a[i] = v; setSection('logical_volumes', a); }}
                    onRemove={() => setSection('logical_volumes', data.logical_volumes.filter((_, idx) => idx !== i))} />
                ))}
              </div>
            </div>
          )}

          {/* ── Tab: Relación entre capas ── */}
          {activeTab === 'layers' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Relación entre capas</h3>
                {autoLayerMap ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--sdp-ok)', fontWeight: 600 }}>✓ Mapa generado automáticamente a partir de los datos registrados</span>
                      <button type="button" className="dv-btn dv-btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                        onClick={() => setSection('layer_map', autoLayerMap)}>
                        Copiar a notas ↓
                      </button>
                    </div>
                    <pre style={{ fontSize: '0.78rem', color: 'var(--text-primary)', background: 'var(--sdp-ok-bg)', border: '1px solid var(--sdp-ok-bd)', padding: '0.75rem', borderRadius: 6, marginBottom: '1.25rem', whiteSpace: 'pre-wrap' }}>{autoLayerMap}</pre>
                  </>
                ) : (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                    El mapa se generará automáticamente cuando añadas discos, arrays RAID y volúmenes.
                  </div>
                )}
                <label className="form-label">Notas adicionales / relación manual</label>
                <textarea className="form-control" rows={5} value={data.layer_map}
                  onChange={e => { setSection('layer_map', e.target.value); }}
                  placeholder="Notas adicionales sobre la relación entre capas..." />
              </div>
            </div>
          )}

          {/* ── Tab: Observaciones ── */}
          {activeTab === 'observations' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Observaciones y alertas</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Discos en mal estado</label>
                    <textarea className="form-control" rows={3} value={obs.failed_disks}
                      onChange={e => setObs('failed_disks', e.target.value)} placeholder="Slot 2 — SMART Failed" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Arrays degradados</label>
                    <textarea className="form-control" rows={3} value={obs.degraded_arrays}
                      onChange={e => setObs('degraded_arrays', e.target.value)} placeholder="md1 — Degraded, falta 1 disco" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Pools con errores</label>
                    <textarea className="form-control" rows={3} value={obs.pool_errors}
                      onChange={e => setObs('pool_errors', e.target.value)} placeholder="tank — checksum errors: 3" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Configuraciones especiales</label>
                    <textarea className="form-control" rows={3} value={obs.special_configs}
                      onChange={e => setObs('special_configs', e.target.value)} placeholder="Disco del SO excluido del RAID..." />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label" style={{ color: 'var(--sdp-fail)' }}>Riesgos detectados</label>
                    <textarea className="form-control" rows={3} value={obs.risks}
                      onChange={e => setObs('risks', e.target.value)}
                      placeholder="Sin hot spare. Un fallo adicional destruiría el array..." />
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServerDisksPanel;
